import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Loader2, Tags, CheckCircle2, AlertTriangle, FileDown, FileSpreadsheet, MapPinned, Ruler } from "lucide-react";
import { saveBlob, pdfFromBase64, missingCsv } from "@/lib/labels";
import { SheetPositionPicker } from "@/components/labels/SheetPositionPicker";
import { PrinterFineTune, loadOffset, saveOffset } from "@/components/labels/PrinterFineTune";

const FIELDS = [["business", "Business Name"], ["contact", "Contact Name"], ["street", "Street Address"], ["city_state_zip", "City, State ZIP"]];
const DEFAULT_INC = { business: true, contact: false, street: true, city_state_zip: true };

const Radio = ({ checked, disabled, onSelect, children, testid }) => (
  <button type="button" disabled={disabled} onClick={onSelect}
    className={`w-full text-left flex items-start gap-3 rounded-xl border px-3.5 py-2.5 transition-colors duration-200 ${checked ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    data-testid={testid} aria-pressed={checked}>
    <span className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${checked ? "border-primary" : "border-muted-foreground/50"}`}>{checked && <span className="w-2 h-2 rounded-full bg-primary" />}</span>
    <span className="text-sm">{children}</span>
  </button>
);

const Section = ({ title, children }) => (
  <div className="space-y-2"><div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{title}</div>{children}</div>
);

export const MailingLabelsDialog = ({ open, onOpenChange, filteredIds = null, selectedIds = [], onFillZips }) => {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("avery-5163");
  const [inc, setInc] = useState(DEFAULT_INC);
  const [attn, setAttn] = useState(false);
  const [scope, setScope] = useState("all");
  const [allCount, setAllCount] = useState(null);
  const [pct, setPct] = useState(null);
  const [result, setResult] = useState(null);
  const [start, setStart] = useState(1);
  const [testing, setTesting] = useState(false);
  const timer = useRef(null);
  const tpl = templates.find((t) => t.id === template);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  useEffect(() => { setStart(1); setOffset(loadOffset(template)); }, [template]);
  const changeOffset = (o) => { setOffset(o); saveOffset(template, o); };
  const offsetBody = { offset_x: offset.x, offset_y: offset.y };

  useEffect(() => {
    if (!open) { clearInterval(timer.current); return; }
    setResult(null); setPct(null);
    setScope(selectedIds.length ? "selected" : filteredIds?.length ? "filtered" : "all");
    setStart(1);
    api.get("/labels/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/labels/prospect-count").then(({ data }) => setAllCount(data.count)).catch(() => setAllCount(null));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = scope === "all" ? allCount : scope === "filtered" ? (filteredIds || []).length : selectedIds.length;
  const anyField = Object.values(inc).some(Boolean);

  const generate = async () => {
    setResult(null); setPct(5);
    timer.current = setInterval(() => setPct((p) => (p == null ? p : Math.min(90, p + 7))), 250);
    try {
      const ids = scope === "filtered" ? filteredIds : scope === "selected" ? selectedIds : [];
      const { data } = await api.post("/labels/mailing", { template, include: inc, attn, scope: scope === "all" ? "all" : "ids", ids, start_position: start, ...offsetBody });
      setPct(100);
      if (data.generated > 0) saveBlob(pdfFromBase64(data.pdf_base64), data.filename);
      setResult(data);
      if (data.generated > 0) toast.success(`${data.generated} label${data.generated === 1 ? "" : "s"} ready \u2014 downloading ${data.filename}`);
      else toast.error("No labels generated \u2014 every prospect is missing address data");
    } catch (e) { setPct(null); toast.error(apiError(e.response?.data?.detail)); }
    finally { clearInterval(timer.current); }
  };

  const alignmentTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/labels/alignment-test", { template, ...offsetBody });
      saveBlob(pdfFromBase64(data.pdf_base64), data.filename);
      toast.success("Alignment test downloaded — print at 100% on plain paper and hold it over a label sheet");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const busy = pct != null && pct < 100;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="mailing-labels-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl"><Tags className="w-5 h-5 text-primary" />Generate Mailing Labels</DialogTitle>
          <DialogDescription>Create a print-ready PDF of mailing labels using the currently filtered prospect list.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
          <Section title="Label template">
            <div className="space-y-2" data-testid="labels-templates">
              {templates.map((t) => (
                <Radio key={t.id} checked={template === t.id} disabled={!t.available} onSelect={() => setTemplate(t.id)} testid={`labels-template-${t.id}`}>
                  <span className="font-medium">{t.name}</span> <span className="text-muted-foreground">({t.size}{t.per_sheet ? ` \u00b7 ${t.per_sheet} per sheet` : ""})</span>{!t.available && <span className="ml-1 text-[10px] font-semibold uppercase rounded-full bg-muted px-2 py-0.5">Future</span>}
                </Radio>
              ))}
            </div>
          </Section>
          <Section title="Start position (partly used sheet)">
            <p className="text-xs text-muted-foreground">Click the first empty label on your sheet. Labels before it are left blank.</p>
            <SheetPositionPicker cols={tpl?.cols} rows={tpl?.rows} value={start} onChange={setStart} />
          </Section>
          <Section title="Printer fine-tune">
            <p className="text-xs text-muted-foreground">If the alignment test prints slightly off, nudge the text toward where it should be, then print the test again.</p>
            <PrinterFineTune value={offset} onChange={changeOffset} />
          </Section>
          </div>
          <div className="space-y-6">
            <Section title="Include fields">
              <div className="space-y-2">
                {FIELDS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer"><Checkbox checked={inc[k]} onCheckedChange={(v) => setInc((s) => ({ ...s, [k]: !!v }))} data-testid={`labels-field-${k}`} />{l}</label>
                ))}
                <label className={`flex items-center gap-2.5 text-sm pl-6 ${inc.contact ? "cursor-pointer" : "opacity-50"}`}><Checkbox checked={attn} disabled={!inc.contact} onCheckedChange={(v) => setAttn(!!v)} data-testid="labels-field-attn" />Add "Attn:" before contact name</label>
              </div>
            </Section>
            <Section title="Prospect selection">
              <div className="space-y-2">
                <Radio checked={scope === "all"} onSelect={() => setScope("all")} testid="labels-scope-all">All Prospects</Radio>
                <Radio checked={scope === "filtered"} disabled={!filteredIds} onSelect={() => setScope("filtered")} testid="labels-scope-filtered">Current Filter Results Only{!filteredIds && <span className="text-muted-foreground"> — open from the Prospects page</span>}</Radio>
                <Radio checked={scope === "selected"} disabled={!selectedIds.length} onSelect={() => setScope("selected")} testid="labels-scope-selected">Selected Prospects{!selectedIds.length && <span className="text-muted-foreground"> — tick rows on the Prospects list</span>}</Radio>
              </div>
              <div className="text-sm font-semibold text-primary tabular-nums" data-testid="labels-selected-count">{count == null ? "Counting\u2026" : `${count} prospect${count === 1 ? "" : "s"} selected`}</div>
            </Section>
          </div>
        </div>

        {pct != null && pct < 100 && (
          <div className="space-y-1.5" data-testid="labels-progress"><Progress value={pct} className="h-2" /><div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Generating your labels…</div></div>
        )}

        {result && (
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="labels-result">
            <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Generated: <b data-testid="labels-generated-count">{result.generated} labels</b><span className="text-muted-foreground">({result.pages} page{result.pages === 1 ? "" : "s"})</span></div>
            <div className="flex items-center gap-2 text-sm"><AlertTriangle className={`w-4 h-4 ${result.skipped ? "text-amber-500" : "text-muted-foreground"}`} />Skipped: <b data-testid="labels-skipped-count">{result.skipped} prospect{result.skipped === 1 ? "" : "s"} missing address data</b></div>
            <div className="flex flex-wrap gap-2">
              {result.generated > 0 && <Button size="sm" variant="outline" onClick={() => saveBlob(pdfFromBase64(result.pdf_base64), result.filename)} data-testid="labels-download-pdf"><FileDown className="w-4 h-4 mr-1.5" />Download PDF again</Button>}
              {result.skipped > 0 && <Button size="sm" variant="outline" onClick={() => saveBlob(missingCsv(result.skipped_rows), "Missing_Addresses.csv")} data-testid="labels-download-missing-csv"><FileSpreadsheet className="w-4 h-4 mr-1.5" />Missing_Addresses.csv</Button>}
              {result.skipped > 0 && onFillZips && <Button size="sm" variant="outline" onClick={onFillZips} data-testid="labels-open-fill-zips"><MapPinned className="w-4 h-4 mr-1.5" />Fill Missing ZIP Codes</Button>}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={alignmentTest} disabled={busy || testing || !tpl?.available} data-testid="labels-alignment-test-btn">{testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ruler className="w-4 h-4 mr-2" />}Print alignment test</Button>
          <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="labels-cancel">{result ? "Close" : "Cancel"}</Button>
          <Button onClick={generate} disabled={busy || !anyField || !count} data-testid="labels-generate-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}Generate PDF</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
